import { PaymentMethodEnum } from '@/types/common';
import type { ICheckoutContext } from '@/types/context';
import type { AdyenPaymentResponse } from '@/types/payment';
import { Errors } from '@/constants/errors';
import {
  buildAdyenGenericPaymentRequest,
  buildAdyenSavedCreditCardPaymentRequest,
  createAdyenCardPayment,
} from '@/utils/adyen';
import type { RefObject } from 'react';
import type { AdyenCardComponent } from '@/types/adyen';
import { checkIfServerFinalizeFailure } from '@/utils/error';

const getPaymentMethod = (checkout: ICheckoutContext): string | undefined => {
  return checkout.paymentMethodSelected === PaymentMethodEnum.OTHER
    ? checkout.otherMethodSelected
    : checkout.paymentMethodSelected;
};

const getAdyenSettings = (checkout: ICheckoutContext, paymentMethod: string) => {
  return checkout.paymentMethodsSettings.find((pm) => pm.paymentMethodName === paymentMethod);
};

export const handleAdyenCardPayment = async (
  checkout: ICheckoutContext,
  sendPaymentToServer: <TResponse>(params: unknown) => Promise<TResponse>,
  params: Record<string, unknown>
): Promise<AdyenPaymentResponse> => {
  try {
    const adyenSettings = getAdyenSettings(checkout, PaymentMethodEnum.CREDIT_CARD);

    if (!adyenSettings) {
      throw new Error(Errors.payment.paymentFailed);
    }

    const selectedCard = checkout.card.selectedCard;

    if (selectedCard && !params.newCard) {
      return await sendPaymentToServer(
        buildAdyenSavedCreditCardPaymentRequest(selectedCard, checkout.customer, adyenSettings, params.cvc as string)
      );
    }

    const cardDetailsRef = checkout.card.cardElementRef as RefObject<AdyenCardComponent>;
    cardDetailsRef.current?.submit();
    await new Promise((resolve) => setTimeout(resolve, 500));

    const request = createAdyenCardPayment({
      browserInfo: checkout.card.adyenCardDetailsRef.current.browserInfo ?? {},
      cardDetails: checkout.card.adyenCardDetailsRef.current,
      paymentMethodId: adyenSettings.id,
      paymentMethodDisplayName: adyenSettings.paymentMethodName,
      email: checkout.customer.email ?? '',
      cardholderName: (checkout.card.adyenCardDetailsRef.current?.paymentMethodData?.holderName as string) ?? '',
      shouldSaveCard: checkout.card.initialCardPayload?.isSaveCard ?? false,
      paymentMethod: {
        type: PaymentMethodEnum.CREDIT_CARD,
      },
    });
    return await sendPaymentToServer(request);
  } catch (error) {
    checkout.card.adyenCardDetailsRef.current?.actions?.reject();
    if (checkIfServerFinalizeFailure(error)) {
      throw error;
    }
    console.error(Errors.payment.paymentFailed, { cause: error });
    throw new Error(Errors.payment.paymentFailed);
  }
};

export const handleAdyenPayment = async (
  checkout: ICheckoutContext,
  sendPaymentToServer: <TResponse>(params: unknown) => Promise<TResponse>,
  params?: Record<string, string>
): Promise<AdyenPaymentResponse> => {
  const paymentMethod = getPaymentMethod(checkout);

  if (!paymentMethod || !params) {
    throw new Error(Errors.payment.paymentFailed);
  }

  const adyenSettings = getAdyenSettings(checkout, paymentMethod);

  if (!adyenSettings) {
    throw new Error(Errors.payment.paymentFailed);
  }

  const request = buildAdyenGenericPaymentRequest(params, adyenSettings, checkout.customer);

  return await sendPaymentToServer(request);
};
